/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Doughnut } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftBadgeDot from "components/SoftComponents/SoftBadgeDot";
import SoftButton from "components/SoftComponents/SoftButton";

// UptimeDoughnutChart configurations
import configs from "./config";
import { uptimeChartStyle, uptimeDoughnutStyle, uptimeLabelsStyle } from "./styles";
import { processUptimeCounts } from "utils/device";
import { processDevicesData } from "utils/device";
import { getUserAccessLevel } from "utils/setup";

// Setting default values for the props of UptimeDoughnutChart
function UptimeDoughnutChart({ color = 'info', icon, title, height = '100%', devices, process = true, user, maxHeight = '100%' }) {

    if (process) {
        var devicesData = devices ? processDevicesData(devices, getUserAccessLevel(user.user.user_type, 'technician')) : [];
    }
    const { count, uptime_status } = devices ? processUptimeCounts(process ? devicesData : devices) : { count: 0, uptime_status: [0, 0] };
    // console.log(uptime_status)

    var uptime_chart_settings = {
        labels: ["Healthy", "Requires Attention"],
        datasets: {
            label: "All Devices",
            backgroundColors: ["success", "error"],
            data: uptime_status
        }
    }

    const { data, options, plugins } = configs(uptime_chart_settings.labels, uptime_chart_settings.datasets || {}, count);
    // console.log(data);

    const renderBadgeDots = uptime_chart_settings.labels
        ? uptime_chart_settings.labels.map((label, index) => {
            const badgeDotKey = `badge-dot-${index}`;

            return (
                <SoftBadgeDot
                    key={badgeDotKey}
                    variant="gradient"
                    color={
                        uptime_chart_settings.datasets.backgroundColors ? uptime_chart_settings.datasets.backgroundColors[index] : "info"
                    }
                    size="xxs"
                    badgeContent={label + " - " + data.datasets[0].data[index]}
                    font={{ color: "text", weight: "medium" }}
                    p={0}
                    display='box'
                />
            );
        })
        : null;

    return (
        <SoftBox py={2} px={2} minHeight='calc(100% - 32px)' sx={(theme) => uptimeDoughnutStyle(theme, height)}>
            <SoftBox zIndex={1} sx={(theme) => uptimeLabelsStyle(theme)}>
                <SoftBox>
                    <SoftBox
                        width="3rem"
                        height="3rem"
                        display="grid"
                        justifyContent="center"
                        alignItems="center"
                        borderRadius="md"
                        shadow="md"
                        color="white"
                        bgColor={color}
                        variant="gradient"
                        className='percent-icon'
                    >
                        <Icon fontSize="default">{icon}</Icon>
                    </SoftBox>
                    <SoftBox className="title" lineHeight={1}>
                        <SoftTypography variant="button" fontWeight="medium" textTransform="capitalize" color="text">{title}</SoftTypography>
                        {count ? (<SoftTypography variant="h5" fontWeight="bold">{count}</SoftTypography>) : null}
                    </SoftBox>
                </SoftBox>
                <SoftBox className="badgeDots">
                    {renderBadgeDots}
                </SoftBox>
            </SoftBox>
            {useMemo(
                () => (
                    <SoftBox zIndex={2} sx={(theme) => uptimeChartStyle(theme, height)}>
                        <Doughnut data={data} options={options} plugins={plugins} style={{ height, width: 'auto', maxHeight }} />
                    </SoftBox>
                ),
                [uptime_chart_settings, height]
            )}
        </SoftBox>
    );
}

// Typechecking props for the UptimeDoughnutChart
UptimeDoughnutChart.propTypes = {
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    devices: PropTypes.array,
    user: PropTypes.object,
    process: PropTypes.bool,
};

export default UptimeDoughnutChart;